import React from 'react';
import { Box, IconButton, LinearProgress, makeStyles, Typography } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useTranslate } from 'react-admin';
import { BREAKPOINTS } from '../../theme/breakpoints';
import { CRM_COLORS, CRM_RADIUS } from '../../theme/crmColors';
import { toFormatDate } from '../../utils/date';
import { removeNamePrefix } from '../notes/subcomponents/ShowNoteDocuments';
import { InstallationFile } from './useInstallationFiles';

const COLUMNS = 'minmax(0, 1fr) 260px 120px 64px';

const useStyles = makeStyles(() => ({
    card: {
        backgroundColor: CRM_COLORS.surface,
        border: `1px solid ${CRM_COLORS.border}`,
        borderRadius: CRM_RADIUS.card,
        boxShadow: CRM_COLORS.shadow,
        overflow: 'hidden',
    },
    head: {
        display: 'grid',
        gridTemplateColumns: COLUMNS,
        gap: 16,
        padding: '11px 18px',
        backgroundColor: CRM_COLORS.surfaceMuted,
        borderBottom: `1px solid ${CRM_COLORS.borderSoft}`,
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '.06em',
        textTransform: 'uppercase' as const,
        color: CRM_COLORS.text.secondary,
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: { display: 'none' },
    },
    actionHead: { textAlign: 'right' as const },
    row: {
        display: 'grid',
        gridTemplateColumns: COLUMNS,
        gap: 16,
        alignItems: 'center',
        padding: '12px 18px',
        borderBottom: `1px solid ${CRM_COLORS.borderSoft}`,
        '&:last-child': { borderBottom: 0 },
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            gap: 8,
        },
    },
    document: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 },
    tile: {
        width: 36,
        height: 36,
        borderRadius: CRM_RADIUS.control,
        backgroundColor: CRM_COLORS.status.info.background,
        color: CRM_COLORS.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    name: {
        fontSize: '0.875rem',
        fontWeight: 500,
        color: CRM_COLORS.text.primary,
        overflowWrap: 'anywhere' as const,
    },
    meta: {
        fontSize: '0.84rem',
        color: CRM_COLORS.text.secondary,
        fontVariantNumeric: 'tabular-nums',
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: { gridColumn: '1 / -1', gridRow: 2 },
    },
    download: {
        justifySelf: 'end',
        width: 40,
        height: 40,
        border: `1px solid ${CRM_COLORS.borderStrong}`,
        borderRadius: CRM_RADIUS.control,
    },
    message: { padding: '16px 18px', fontSize: '0.875rem', color: CRM_COLORS.text.secondary },
}));

interface Props {
    files: InstallationFile[];
    loading: boolean;
    error: any;
}

/** The files of the history: the same notes and medias as the history panel, without opening it. */
const InstallationFiles = ({ files, loading, error }: Props) => {
    const t = useTranslate();
    const classes = useStyles();

    return (
        <Box className={classes.card} role='tabpanel' aria-label={t('app.ancsV2.files.title')}>
            <Box className={classes.head}>
                <span>{t('app.ancsV2.files.document')}</span>
                <span>{t('app.ancsV2.files.step')}</span>
                <span>{t('app.ancsV2.files.date')}</span>
                <span className={classes.actionHead}>{t('app.ancsV2.files.action')}</span>
            </Box>
            {error ? (
                <Typography className={classes.message}>{t('app.ancsV2.files.error')}</Typography>
            ) : loading ? (
                <LinearProgress />
            ) : files.length === 0 ? (
                <Typography className={classes.message}>{t('app.ancsV2.files.empty')}</Typography>
            ) : (
                files.map(({ media, Icon, note }) => {
                    const name = media.name ? removeNamePrefix(media.name) : 'document';
                    return (
                        <Box key={media.urlToDownload} className={classes.row}>
                            <Box className={classes.document}>
                                <span className={classes.tile} aria-hidden>
                                    <Icon fontSize='small' />
                                </span>
                                <Typography className={classes.name}>{name}</Typography>
                            </Box>
                            <Typography className={classes.meta}>{note?.name}</Typography>
                            <Typography className={classes.meta}>
                                {note?.createdAt ? toFormatDate(note.createdAt, 'DD/MM/YYYY') : ''}
                            </Typography>
                            <IconButton
                                className={classes.download}
                                size='small'
                                color='primary'
                                aria-label={`${t('app.ancsV2.files.download')} ${name}`}
                                onClick={() => window.open(media.urlToDownload, '_blank')}
                            >
                                <GetAppIcon fontSize='small' />
                            </IconButton>
                        </Box>
                    );
                })
            )}
        </Box>
    );
};

export default InstallationFiles;
